import React, { useEffect, useState } from "react";
import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { navigate } from "../../lib/router";
import { useLanguage } from "../../contexts/LanguageContext";
import { settingsLabels } from "./labels";
import { ChevronLeft, Plus, Save, Trash2, Shield } from "lucide-react";
import { NewInsuranceProviderModal } from "./components/NewInsuranceProviderModal";
import { SectionCard } from "../../components/SectionCard";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

interface InsuranceProvider {
  id: number;
  company_id: number;
  name: string;
  is_default: boolean;
  active: boolean;
}

export default function InsuranceProvidersPage() {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].insuranceProviders;

  const [providers, setProviders] = useState<InsuranceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [newProviderName, setNewProviderName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await api.listInsuranceProviders();
    if (res.ok) {
      setProviders(res.data);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addProvider = async () => {
    if (!newProviderName.trim()) return;
    const res = await api.createInsuranceProvider({ name: newProviderName.trim() });
    if (res.ok) {
      toast.success(t.toasts.success);
      setNewProviderName("");
      setIsModalOpen(false);
      load();
    } else {
      toast.error(t.toasts.error);
    }
  };

  const deleteProvider = async () => {
    if (!deletingId) return;
    const res = await api.deleteInsuranceProvider(deletingId);
    if (res.ok) {
      toast.success(t.toasts.deleted);
      load();
    } else {
      toast.error(t.toasts.deleteError);
    }
    setDeletingId(null);
  };

  const startEdit = (provider: InsuranceProvider) => {
    setEditingId(provider.id);
    setEditingName(provider.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveEdit = async (id: number) => {
    if (!editingName.trim()) return;
    const res = await api.updateInsuranceProvider(id, { name: editingName.trim() });
    if (res.ok) {
      toast.success(t.toasts.updated);
      setEditingId(null);
      setEditingName("");
      load();
    } else {
      toast.error(t.toasts.error);
    }
  };

  const sortedProviders = [...providers].sort((a, b) => {
    if (a.is_default) return -1;
    if (b.is_default) return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="mx-auto w-full max-w-5xl p-4 md:p-6 space-y-10 animate-in fade-in duration-500">
      {/* Premium Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/app/settings")}
            className="rounded-xl hover:bg-neutral-100 transition-all active:scale-95"
          >
            <ChevronLeft className="h-6 w-6 text-neutral-600" />
          </Button>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-neutral-800 tracking-tight">{t.header}</h1>
            <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest leading-none">
              {t.description}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => setIsModalOpen(true)}
            className="h-12 px-8 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-widest rounded-2xl transition-all active:scale-95"
          >
            <Plus className="h-5 w-5 mr-2" />
            {lang === 'pt' ? 'Adicionar' : 'Add'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex flex-col items-center gap-4 text-center">
          <div className="h-10 w-10 border-4 border-emerald-100 border-t-emerald-600 rounded-full animate-spin" />
          <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">{t.loading}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedProviders.length === 0 ? (
            <div className="rounded-[2.5rem] border-2 border-dashed border-neutral-100 p-20 flex flex-col items-center justify-center gap-4 text-center grayscale opacity-60">
               <div className="w-20 h-20 rounded-[2.5rem] bg-neutral-50 flex items-center justify-center">
                  <Shield className="h-10 w-10 text-neutral-300" />
               </div>
               <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">{t.none}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {sortedProviders.map(provider => (
                <div 
                  key={provider.id} 
                  className="group flex items-center justify-between p-6 bg-white border border-neutral-100 rounded-[2rem] shadow-sm hover:border-neutral-200 transition-all duration-300 animate-in slide-in-from-bottom-4"
                >
                  {editingId === provider.id ? (
                    <div className="flex items-center gap-3 flex-1">
                      <Input 
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        className="h-12 max-w-sm rounded-xl border-neutral-100 font-bold"
                        autoFocus
                        onKeyDown={e => e.key === 'Enter' && saveEdit(provider.id)}
                      />
                      <Button 
                        onClick={() => saveEdit(provider.id)}
                        className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-all active:scale-95"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {lang === 'pt' ? 'Salvar' : 'Save'}
                      </Button>
                      <Button 
                        variant="ghost" 
                        onClick={cancelEdit}
                        className="h-12 px-4 font-bold text-neutral-400 hover:text-neutral-600 rounded-xl"
                      >
                        {lang === 'pt' ? "Cancelar" : "Cancel"}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col gap-1">
                        <div className="text-sm font-bold text-neutral-800 uppercase tracking-widest">{provider.name}</div>
                        {!!provider.is_default && (
                          <span className="w-fit inline-flex items-center px-2.5 py-0.5 rounded-lg text-[9px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-600 border border-emerald-100">
                            {t.default}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!provider.is_default && (
                          <>
                            <Button 
                              variant="ghost" 
                              onClick={() => startEdit(provider)}
                              className="h-12 px-4 font-bold text-[10px] uppercase tracking-widest text-neutral-400 hover:text-neutral-800 hover:bg-neutral-50 rounded-xl transition-all"
                            >
                              {t.edit}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => setDeletingId(provider.id)}
                              className="h-12 w-12 rounded-xl text-neutral-300 hover:text-red-600 hover:bg-red-50 transition-all active:scale-90"
                            >
                              <Trash2 className="h-5 w-5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={!!deletingId} onOpenChange={open => !open && setDeletingId(null)}>
        <AlertDialogContent className="rounded-[2.5rem] border-none shadow-2xl p-8">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-neutral-800 tracking-tight">
              {t.toasts.deleteDialog.title}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <p className="text-sm font-bold text-neutral-400 uppercase tracking-widest leading-relaxed mt-2">
            {t.toasts.deleteDialog.description}
          </p>
          <AlertDialogFooter className="mt-8 gap-3">
            <AlertDialogCancel className="h-12 px-6 rounded-2xl border-neutral-100 font-bold uppercase text-[10px] tracking-widest hover:bg-neutral-50 transition-all">
              {t.toasts.deleteDialog.cancel}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteProvider} 
              className="h-12 px-8 bg-red-600 hover:bg-red-700 text-white font-bold uppercase text-[10px] tracking-widest rounded-2xl transition-all active:scale-95"
            >
              {t.toasts.deleteDialog.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NewInsuranceProviderModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        newProviderName={newProviderName}
        setNewProviderName={setNewProviderName}
        onAdd={addProvider}
        loading={false}
      />
    </div>
  );
}